import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { CurrencyIcon } from '@root/entities/currency/ui'
import { Input, InputPercentage } from '@root/shared/ui-kit'

import { ControlsQuickOption } from '../../common'

import type { Pair } from '@root/entities/pair'
import type { CurrencyInputModel } from '@root/features/currency/currency-input-reatom'

type Props = {
  pair: Pair.PairMinimal
  model: CurrencyInputModel
}

const SELL_QUICK_OPTIONS = [0.25, 0.5, 0.75, 1]

export const TradingControlsAmountSell: React.FC<Props> = ({ pair, model }) => {
  const { t } = useTranslation()
  const [percentageRaw, setPercentageRaw] = useState<string>('0')
  const [amountRaw] = useAtom(model.amountRawAtom)
  const [amountBalancePercentage] = useAtom(model.amountBalancePercentageAtom)

  const setQuickAmount = useAction(model.setQuickAmount)
  const setSellAmountRaw = useAction(model.setAmountRawAction)

  const renderQuickOptions = () => (
    <QuickOptions>
      {SELL_QUICK_OPTIONS.map((it) => {
        const percentage = it * 100
        const isSelected = amountBalancePercentage.toFixed(2) === it.toFixed(2)

        return (
          <ControlsQuickOption
            title={`${percentage}%`}
            key={it}
            active={isSelected}
            onClick={() => {
              setPercentageRaw(percentage.toString())
              setQuickAmount(it)
            }}
          />
        )
      })}
    </QuickOptions>
  )

  return (
    <Root>
      <Percentage>
        <Input
          numeric
          startContent={<span>{t('common.amount')}</span>}
          decimalScale={pair.quote.decimals}
          onChange={(e) => setSellAmountRaw(e.target.value)}
          value={amountRaw}
          size="m"
          placeholder="0"
          endContent={
            <CurrencyIcon
              currency={pair.quote}
              withGoogleLens={false}
              size={24}
              css={css`
                justify-self: end;
              `}
            />
          }
        />
        <InputPercentage
          value={percentageRaw}
          onChange={({ value, percents }) => {
            setPercentageRaw(value)
            if (typeof percents === 'number') {
              setQuickAmount(percents)
            }
          }}
          size="m"
        />
      </Percentage>

      {renderQuickOptions()}
    </Root>
  )
}

const Root = styled.div`
  gap: 6px;

  display: grid;
  overflow: hidden;
  align-items: center;

  margin-inline: 10px;
`

const Percentage = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px;
  gap: 4px;
`

const QuickOptions = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: 4px;
`
